<template>
	<div class="animated fadeIn dash">

		<!-- Header + range -->
		<div class="dash-head">
			<div>
				<h4 class="mb-0">Monitoring overview</h4>
				<small class="text-muted">{{ rangeCaption }}</small>
			</div>
			<div class="range">
				<button v-for="r in ranges" :key="r.days" type="button"
								class="range-btn" :class="{active: !custom && days === r.days}"
								@click="pickRange(r.days)">{{ r.label }}</button>
				<button type="button" class="range-btn" :class="{active: custom}" @click="custom = !custom">
					Custom
				</button>
			</div>
		</div>

		<div v-if="custom" class="custom-range">
			<label for="dash-from">From</label>
			<input id="dash-from" type="date" v-model="from" class="form-control form-control-sm"/>
			<label for="dash-to">To</label>
			<input id="dash-to" type="date" v-model="to" class="form-control form-control-sm"/>
			<b-button size="sm" variant="primary" @click="load" :disabled="!from || !to">Apply</b-button>
		</div>

		<div v-if="loading && !loaded" class="text-center py-5">
			<b-spinner variant="info" style="width:3rem;height:3rem;"></b-spinner>
		</div>

		<div v-else>
			<b-alert v-if="loadError" show variant="warning" class="small">{{ loadError }}</b-alert>

			<template v-else>
				<!-- Nothing recorded yet -->
				<b-alert v-if="!summary.hasData" show variant="light" class="border small text-muted">
					<i class="fa fa-info-circle mr-1 text-primary"></i>
					<strong>No health checks recorded for this period.</strong>
					{{ summary.reason }}
					<span v-if="summary.systemsCheckable === 0">
						Add a health address on <router-link to="/monitoring/systems">Systems</router-link>
						and figures will start appearing here within a few minutes.
					</span>
				</b-alert>

				<!-- Headline numbers -->
				<div class="kpis">
					<div class="kpi kpi-primary">
						<div class="kpi-value">{{ percent(totals.uptimePercent) }}</div>
						<div class="kpi-label">Uptime</div>
						<div class="kpi-foot">{{ formatNumber(totals.up) }} of {{ formatNumber(totals.checks) }} checks healthy</div>
					</div>
					<div class="kpi" :class="{'kpi-bad': totals.down > 0}">
						<div class="kpi-value">{{ formatNumber(totals.down) }}</div>
						<div class="kpi-label">Failed checks</div>
						<div class="kpi-foot">≈ {{ formatNumber(totals.estimatedDownMinutes) }} min unavailable</div>
					</div>
					<div class="kpi" :class="{'kpi-warn': totals.degraded > 0}">
						<div class="kpi-value">{{ formatNumber(totals.degraded) }}</div>
						<div class="kpi-label">Slow responses</div>
						<div class="kpi-foot">Replied, but over the limit</div>
					</div>
					<div class="kpi">
						<div class="kpi-value">{{ summary.systemsCheckable }}<span class="kpi-of">/{{ summary.systemsTotal }}</span></div>
						<div class="kpi-label">Systems watched</div>
						<div class="kpi-foot">{{ summary.systemsTotal - summary.systemsCheckable }} without a health address</div>
					</div>
				</div>

				<p class="estimate-note">
					Uptime is the share of checks that came back healthy. Minutes unavailable is an
					estimate — failed checks × the {{ Math.round((totals.estimateBasedOnIntervalMs || 60000) / 1000) }}s
					check interval — not a measured duration.
				</p>

				<b-row>
					<!-- Donut -->
					<b-col lg="4" class="mb-3">
						<b-card no-body class="h-100">
							<div slot="header" class="card-head">Check results</div>
							<b-card-body>
								<div v-if="!totals.checks" class="text-muted small py-4 text-center">Nothing to show yet.</div>
								<template v-else>
									<canvas ref="donut" height="180"></canvas>
									<ul class="legend">
										<li v-for="s in summary.statusShare" :key="s.key">
											<span class="swatch" :class="'swatch-' + s.key"></span>
											{{ s.label }}
											<span class="legend-value">{{ formatNumber(s.count) }}</span>
										</li>
									</ul>
								</template>
							</b-card-body>
						</b-card>
					</b-col>

					<!-- Daily trend -->
					<b-col lg="8" class="mb-3">
						<b-card no-body class="h-100">
							<div slot="header" class="card-head">Checks per day</div>
							<b-card-body>
								<div v-if="!daily.length" class="text-muted small py-4 text-center">Nothing to show yet.</div>
								<canvas v-show="daily.length" ref="trend" height="150"></canvas>
							</b-card-body>
						</b-card>
					</b-col>
				</b-row>

				<!-- Uptime by system -->
				<b-card no-body class="mb-3">
					<div slot="header" class="card-head">Uptime by system <span class="card-head-note">worst first</span></div>
					<div class="table-responsive">
						<table class="table table-sm mb-0 uptime-table">
							<thead>
								<tr>
									<th>System</th>
									<th>Group</th>
									<th style="min-width:180px">Uptime</th>
									<th class="text-right">Checks</th>
									<th class="text-right">Failed</th>
									<th class="text-right">Slow</th>
									<th class="text-right">Avg response</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="s in perSystem" :key="s.code">
									<td>{{ s.name }}</td>
									<td class="small text-muted">{{ groupLabel(s.category) }}</td>
									<td>
										<div v-if="s.uptimePercent !== null" class="bar-row">
											<div class="bar"><div class="bar-fill" :class="barClass(s.uptimePercent)"
																						 :style="{width: s.uptimePercent + '%'}"></div></div>
											<span class="bar-value">{{ percent(s.uptimePercent) }}</span>
										</div>
										<span v-else class="text-muted small">
											{{ s.checkable ? 'Not checked yet' : 'No health address' }}
										</span>
									</td>
									<td class="text-right num">{{ formatNumber(s.checks) }}</td>
									<td class="text-right num" :class="{'text-danger': s.down > 0}">{{ formatNumber(s.down) }}</td>
									<td class="text-right num" :class="{'text-warning': s.degraded > 0}">{{ formatNumber(s.degraded) }}</td>
									<td class="text-right num">{{ s.avgResponseMs !== null && s.avgResponseMs !== undefined ? s.avgResponseMs + ' ms' : '—' }}</td>
								</tr>
								<tr v-if="!perSystem.length">
									<td colspan="7" class="text-center text-muted py-4">No systems registered.</td>
								</tr>
							</tbody>
						</table>
					</div>
				</b-card>

				<!-- Incidents -->
				<b-card no-body>
					<div slot="header" class="card-head">
						Incidents <span class="card-head-note">{{ incidents.length }} in this period</span>
					</div>
					<div class="table-responsive">
						<table class="table table-sm table-striped mb-0">
							<thead>
								<tr>
									<th>When</th>
									<th>System</th>
									<th>Result</th>
									<th class="text-right">Response</th>
									<th>What happened</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="i in incidents" :key="i.id">
									<td class="small text-muted">{{ formatDateTime(i.checked_at) }}</td>
									<td>{{ nameFor(i.system_code) }}</td>
									<td>
										<span class="state" :class="'state-' + i.status">
											<span class="dot"></span>{{ i.status === 'DOWN' ? 'Not responding' : 'Slow' }}
										</span>
									</td>
									<td class="text-right num">
										<span v-if="i.response_time_ms !== null && i.response_time_ms !== undefined">{{ i.response_time_ms }} ms</span>
										<span v-else class="text-muted">—</span>
									</td>
									<td class="small text-muted">{{ i.message || '—' }}</td>
								</tr>
								<tr v-if="!incidents.length">
									<td colspan="5" class="text-center text-muted py-4">
										No failures or slow responses in this period.
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</b-card>
			</template>
		</div>
	</div>
</template>

<script>
import Chart from 'chart.js';
import monitoringService from '@/services/monitoringService';

const GROUP_LABELS = {
	HRM: 'HRM',
	CS: 'Device Portal',
	ADMIN: 'Admin',
	OTHER: 'Other'
};

// Same status colours as the Live board, so a green here means what a green means there.
const COLORS = {
	UP: '#1b7a50',
	DEGRADED: '#9a6606',
	DOWN: '#b33b2e'
};

export default {
	name: 'MonitoringDashboard',

	data() {
		return {
			loading: false,
			loaded: false,
			loadError: '',

			ranges: [
				{days: 1, label: '1 day'},
				{days: 7, label: '7 days'},
				{days: 15, label: '15 days'},
				{days: 30, label: '30 days'}
			],
			days: 7,
			custom: false,
			from: '',
			to: '',

			summary: emptySummary(),
			donutInst: null,
			trendInst: null
		};
	},

	computed: {
		totals() { return this.summary.totals || {}; },
		perSystem() { return this.summary.perSystem || []; },
		daily() { return this.summary.daily || []; },
		incidents() { return this.summary.incidents || []; },

		rangeCaption() {
			if (!this.summary.from) return 'Reliability over the selected period';
			return this.summary.from === this.summary.to
				? this.summary.from
				: this.summary.from + ' to ' + this.summary.to;
		}
	},

	created() {
		this.load();
	},

	beforeDestroy() {
		if (this.donutInst) this.donutInst.destroy();
		if (this.trendInst) this.trendInst.destroy();
	},

	methods: {
		pickRange(days) {
			this.custom = false;
			this.days = days;
			this.load();
		},

		load() {
			this.loading = true;
			this.loadError = '';

			const params = this.custom && this.from && this.to
				? {from: this.from, to: this.to}
				: {days: this.days};

			monitoringService.getSummary(params, (data) => {
				this.loading = false;
				this.loaded = true;

				if (!data || data.available === false) {
					this.loadError = (data && data.error) || 'The summary is not available right now.';
					return;
				}

				this.summary = Object.assign(emptySummary(), data);
				// Keep the custom inputs in step with whatever range the server actually used.
				this.from = data.from || this.from;
				this.to = data.to || this.to;

				this.$nextTick(() => {
					this.renderDonut();
					this.renderTrend();
				});
			}, (err) => {
				this.loading = false;
				this.loaded = true;
				this.loadError = err;
			});
		},

		renderDonut() {
			const ctx = this.$refs.donut;
			if (!ctx || !this.totals.checks) return;

			const share = this.summary.statusShare || [];
			const data = {
				labels: share.map(s => s.label),
				datasets: [{
					data: share.map(s => s.count),
					backgroundColor: share.map(s => COLORS[s.key] || '#5a6b78'),
					borderWidth: 0
				}]
			};

			if (this.donutInst) {
				this.donutInst.data = data;
				this.donutInst.update();
				return;
			}

			this.donutInst = new Chart(ctx, {
				type: 'doughnut',
				data: data,
				options: {
					responsive: true,
					maintainAspectRatio: true,
					cutoutPercentage: 68,
					// The legend below the chart carries the counts, so the built-in one would just
					// repeat itself in a smaller font.
					legend: {display: false},
					tooltips: {
						callbacks: {
							label: (item, d) => {
								const value = d.datasets[0].data[item.index];
								const total = d.datasets[0].data.reduce((a, b) => a + b, 0);
								const pct = total ? Math.round((value / total) * 1000) / 10 : 0;
								return d.labels[item.index] + ': ' + value.toLocaleString() + ' (' + pct + '%)';
							}
						}
					}
				}
			});
		},

		renderTrend() {
			const ctx = this.$refs.trend;
			if (!ctx || !this.daily.length) return;

			const labels = this.daily.map(d => String(d.day).substring(0, 10));
			const data = {
				labels: labels,
				datasets: [
					{label: 'Working', backgroundColor: COLORS.UP, data: this.daily.map(d => d.up_count || 0)},
					{label: 'Slow', backgroundColor: COLORS.DEGRADED, data: this.daily.map(d => d.degraded_count || 0)},
					{label: 'Not responding', backgroundColor: COLORS.DOWN, data: this.daily.map(d => d.down_count || 0)}
				]
			};

			if (this.trendInst) {
				this.trendInst.data = data;
				this.trendInst.update();
				return;
			}

			this.trendInst = new Chart(ctx, {
				type: 'bar',
				data: data,
				options: {
					responsive: true,
					maintainAspectRatio: true,
					// Stacked, because the question is "how did that day go" — the split within a day
					// matters more than comparing categories side by side.
					scales: {
						xAxes: [{stacked: true, gridLines: {display: false}}],
						yAxes: [{stacked: true, ticks: {beginAtZero: true, precision: 0}}]
					},
					legend: {position: 'bottom'}
				}
			});
		},

		nameFor(code) {
			const match = this.perSystem.find(s => s.code === code);
			return match ? match.name : code;
		},

		groupLabel(category) {
			return GROUP_LABELS[category] || category || 'Other';
		},

		barClass(value) {
			if (value >= 99) return 'bar-good';
			if (value >= 95) return 'bar-ok';
			return 'bar-bad';
		},

		percent(value) {
			return value === null || value === undefined ? '—' : value.toFixed(2) + '%';
		},

		formatNumber(value) {
			if (value === null || value === undefined) return '—';
			const n = Number(value);
			return isNaN(n) ? value : n.toLocaleString();
		},

		formatDateTime(value) {
			if (!value) return '—';
			const d = new Date(value);
			return isNaN(d.getTime()) ? value : d.toLocaleString();
		}
	}
};

function emptySummary() {
	return {
		from: '', to: '', hasData: false, reason: '',
		systemsTotal: 0, systemsCheckable: 0, historyAvailable: true,
		totals: {checks: 0, up: 0, degraded: 0, down: 0, uptimePercent: null, incidents: 0, estimatedDownMinutes: 0},
		perSystem: [], daily: [], statusShare: [], incidents: []
	};
}
</script>

<style scoped>
.dash {
	--ink: #16202a;
	--muted: #5d6d7b;
	--edge: #d3dde4;
	--fill: #eff3f6;
	--up: #1b7a50;
	--slow: #9a6606;
	--down: #b33b2e;
}

.dash-head {
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 1.25rem;
}

.range { display: flex; flex-wrap: wrap; gap: 0.3rem; }

.range-btn {
	border: 1px solid var(--edge);
	background: #fff;
	color: var(--muted);
	font-size: 0.8rem;
	padding: 0.28rem 0.75rem;
	border-radius: 4px;
	cursor: pointer;
}

.range-btn:hover { border-color: #b7c4cd; }
.range-btn.active { background: var(--ink); border-color: var(--ink); color: #fff; }
.range-btn:focus-visible { outline: 2px solid var(--up); outline-offset: 2px; }

.custom-range {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
	margin-bottom: 1.25rem;
	padding: 0.75rem 1rem;
	background: var(--fill);
	border: 1px solid var(--edge);
	border-radius: 5px;
}

.custom-range label { margin: 0; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.07em; color: var(--muted); }
.custom-range input { width: auto; }

/* ── KPIs ───────────────────────────────────────────────── */
.kpis {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
	gap: 0.85rem;
	margin-bottom: 0.6rem;
}

.kpi {
	background: #fff;
	border: 1px solid var(--edge);
	border-top: 3px solid var(--edge);
	border-radius: 6px;
	padding: 1rem 1.15rem;
}

.kpi-primary { border-top-color: var(--up); }
.kpi-bad { border-top-color: var(--down); }
.kpi-warn { border-top-color: var(--slow); }

.kpi-value {
	font-size: 1.85rem;
	font-weight: 300;
	line-height: 1.1;
	letter-spacing: -0.02em;
	font-variant-numeric: tabular-nums;
}

.kpi-of { font-size: 1.1rem; color: var(--muted); }

.kpi-label {
	font-size: 0.72rem;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: var(--muted);
	margin-top: 0.35rem;
	font-weight: 600;
}

.kpi-foot { font-size: 0.76rem; color: var(--muted); margin-top: 0.3rem; }

.estimate-note {
	font-size: 0.76rem;
	color: var(--muted);
	margin: 0 0 1.25rem;
	max-width: 78ch;
}

/* ── Cards ──────────────────────────────────────────────── */
.card-head { font-size: 0.85rem; font-weight: 600; }
.card-head-note { font-size: 0.72rem; font-weight: 400; color: var(--muted); margin-left: 0.4rem; }

.legend { list-style: none; padding: 0; margin: 1rem 0 0; }

.legend li {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-size: 0.83rem;
	padding: 0.2rem 0;
}

.legend-value { margin-left: auto; font-variant-numeric: tabular-nums; color: var(--muted); }

.swatch { width: 10px; height: 10px; border-radius: 2px; flex: none; }
.swatch-UP { background: #1b7a50; }
.swatch-DEGRADED { background: #9a6606; }
.swatch-DOWN { background: #b33b2e; }

/* ── Uptime bars ────────────────────────────────────────── */
.bar-row { display: flex; align-items: center; gap: 0.6rem; }

.bar {
	flex: 1;
	height: 8px;
	background: var(--fill);
	border-radius: 4px;
	overflow: hidden;
	min-width: 80px;
}

.bar-fill { height: 100%; }
.bar-good { background: var(--up); }
.bar-ok { background: var(--slow); }
.bar-bad { background: var(--down); }

.bar-value { font-size: 0.8rem; font-variant-numeric: tabular-nums; width: 4.2rem; text-align: right; }

.uptime-table td { vertical-align: middle; }

/* ── Incident state ─────────────────────────────────────── */
.state { display: inline-flex; align-items: center; font-size: 0.8rem; white-space: nowrap; }
.state .dot { width: 8px; height: 8px; border-radius: 50%; margin-right: 0.4rem; background: #c8ced3; }
.state-DOWN { color: var(--down); }
.state-DOWN .dot { background: var(--down); }
.state-DEGRADED { color: var(--slow); }
.state-DEGRADED .dot { background: var(--slow); }

.num { font-variant-numeric: tabular-nums; }
</style>
